// Phase 9 — per-project chat page.
//
// Routes:
//   /projects/:projectId/chat                  — no active session
//   /projects/:projectId/chat/:sessionId       — active session
//
// Composes `SessionSidebar` (per-project list with rename/delete) with
// `ChatPage` (existing chat UI, `hideHistorySidebar=true`) and a
// `ResumeBanner` on the no-sessionId entry point. "+ New" creates a
// fresh session via the project picker's existing `createSession`
// helper and navigates to it.

import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { ChatPage } from "../chat/ChatPage";
import { ImplementFlow } from "../focus/ImplementFlow";
import { useT } from "../i18n/index.js";
import { Banner } from "../ui/Banner";
import { Spinner } from "../ui/Spinner";

import { ResumeBanner } from "./ResumeBanner";
import { SessionSidebar } from "./SessionSidebar";
import { useSessionImplement } from "./useSessions";
import { createSession } from "./useProjects";

interface ProjectChatPageProps {
  readonly projectId: number;
  readonly sessionId?: string;
  readonly onSelectSession: (sessionId: string) => void;
  readonly onBackToPicker: () => void;
}

export function ProjectChatPage({
  projectId,
  sessionId,
  onSelectSession,
  onBackToPicker,
}: ProjectChatPageProps) {
  const t = useT();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // Is the active session an autonomous implement run? If so it reopens on the
  // ImplementFlow surface (re-seeded from the persisted issue), not the generic
  // chat — the "safe to walk away" resume path. Resolves to null for chat
  // sessions and while no session is selected.
  const detail = useSessionImplement(sessionId);

  const onNewSession = async () => {
    setCreateError(null);
    setCreating(true);
    try {
      const { sessionId: nextId } = await createSession(projectId);
      onSelectSession(nextId);
    } catch (err) {
      setCreateError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  if (sessionId === undefined) {
    return (
      <div className="flex h-full min-h-screen">
        <SessionSidebar
          projectId={projectId}
          onPick={onSelectSession}
          onNew={() => void onNewSession()}
        />
        <main className="flex min-w-0 flex-1 flex-col items-center justify-center p-8">
          <div className="w-full max-w-lg space-y-3">
            <ResumeBanner projectId={projectId} onResume={onSelectSession} />
            {createError !== null && (
              <Banner tone="error">{t("projects.startFailed", { error: createError })}</Banner>
            )}
            <div className="rounded-md border border-line bg-surface p-6 text-center">
              <h2 className="text-lg font-semibold">{t("projects.startNewHeading")}</h2>
              <p className="mt-1 text-sm text-ink-muted">
                {t("projects.startNewSubtitle")}
              </p>
              <button
                type="button"
                onClick={() => void onNewSession()}
                disabled={creating}
                className="mt-3 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-on-solid hover:bg-primary-hover disabled:opacity-60"
                data-testid="start-new-session-button"
              >
                {creating ? t("projects.starting") : t("projects.startSession")}
              </button>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={onBackToPicker}
                  className="text-xs text-ink-subtle underline hover:text-ink-3"
                >
                  {t("projects.switchProject")}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const implement = detail.implement;
  const toDashboard = () =>
    navigate({
      to: "/projects/$projectId",
      params: { projectId: String(projectId) },
    });
  const toIssue = (iid: number) =>
    navigate({
      to: "/projects/$projectId/issues/$iid",
      params: { projectId: String(projectId), iid: String(iid) },
    });

  return (
    <div className="flex h-full min-h-screen">
      <SessionSidebar
        projectId={projectId}
        activeSessionId={sessionId}
        onPick={onSelectSession}
        onNew={() => void onNewSession()}
      />
      <div className="flex min-w-0 flex-1">
        {detail.loading ? (
          <div className="flex flex-1 items-center justify-center">
            <Spinner label={t("common.loading")} />
          </div>
        ) : implement ? (
          <ImplementFlow
            key={sessionId}
            resumeSessionId={sessionId}
            projectId={projectId}
            iid={implement.issueIid}
            issueTitle={implement.issueTitle}
            issueWebUrl={implement.issueWebUrl}
            onRestart={toDashboard}
            onOpenIssue={toIssue}
            onRefineInstead={() => toIssue(implement.issueIid)}
          />
        ) : (
          <ChatPage
            sessionId={sessionId}
            onPickSession={onSelectSession}
            onBackToPicker={onBackToPicker}
            hideHistorySidebar
          />
        )}
      </div>
    </div>
  );
}
